import { useState } from 'react'
import type { PolicyArtefactType, PolicyItem, PolicyWithOverride } from '@/lib/apiTypes'
import { useAsync, useInstancePolicies, setPolicyOverride, forceIdentityCustom } from '@/lib/hooks'
import { useToast } from '@/context/ToastContext'
import { api } from '@/lib/api'
import { EmptyState } from '@/shared/components/EmptyState'
import { ConfirmModal } from '@/shared/components/ConfirmModal'
import { PolicyModal } from '@/features/policies/components/PolicyModal'

/** Effective enforcement for an instance, given its override (or inheritance). */
function effective(p: PolicyWithOverride): { label: string; inherited: boolean } {
  const o = p.override
  if (!o || o.inherit) return { label: p.is_enforcing ? 'enforcing' : 'advisory', inherited: true }
  if (o.disabled) return { label: 'off', inherited: false }
  return { label: o.enforcing ? 'enforcing' : 'advisory', inherited: false }
}

/**
 * Identity-level policy authoring for one artefact identity (module / workspace / provider / plan).
 * Surfaces the additive model from docs/policy-bundle-distribution.md:
 *
 *  - **Inherited** = the org-baseline policies (`instance_key IS NULL`). Each can be inherited or
 *    per-instance overridden (Inherit / Off / advisory↔enforcing) — the established 076 controls.
 *  - **Custom** = policies authored just for THIS identity (`instance_key` set). Admins add/edit/
 *    delete them and set enforce/advisory. Inheritance ON = inherited **plus** custom (additive).
 *  - **Force** disables (retains, never deletes) all of the identity's custom policies, so only the
 *    inherited baseline applies. Re-enabling restores the additive set.
 *
 * `visibleSection` shows only one of the two sections (the provider Policies modal renders each
 * behind a tab, mounting this once so the fetch/busy state survives tab switches); omitted = both,
 * the original single-column layout the module/state detail pages use.
 *
 * `instanceKey: null` = ORG MODE: the sections operate on the org baseline itself for the whole
 * artefact type. "Inherited" becomes the applied-baseline list with direct Enable/Disable +
 * Advisory/Enforcing controls (no per-instance override layer), and "Custom" authors org-baseline
 * policies that apply to EVERY identity of the type. Org mode should always pass `visibleSection` -
 * with both sections visible it would list the same baseline twice.
 */
export function PolicyOverrides({ orgId, artefactType, instanceKey, canAdmin, visibleSection }: {
  orgId: string; artefactType: PolicyArtefactType; instanceKey: string | null; canAdmin: boolean
  visibleSection?: 'inherited' | 'custom'
}) {
  const isOrgMode = instanceKey === null
  const instanceData = useInstancePolicies(orgId, artefactType, instanceKey)
  // Org-baseline list for org mode (the /policies list is already instance_key IS NULL only).
  // Both hooks always run (rules of hooks); the inactive one short-circuits to an empty list.
  const orgData = useAsync<{ policies: PolicyItem[] }>(
    () => isOrgMode
      ? api.get(`/api/orgs/${orgId}/policies?artefact_type=${artefactType}`)
      : Promise.resolve({ policies: [] }),
    [orgId, artefactType, isOrgMode],
  )
  const { loading, refetch } = isOrgMode ? orgData : instanceData
  const { showToast } = useToast()
  const [busy, setBusy] = useState<string | null>(null)
  const [showCreate, setShowCreate] = useState(false)
  const [editPolicy, setEditPolicy] = useState<PolicyWithOverride | null>(null)
  const [confirmDelete, setConfirmDelete] = useState<PolicyWithOverride | null>(null)

  const policies: PolicyWithOverride[] = isOrgMode
    ? (orgData.data?.policies ?? []).map((p) => ({ ...p, override: null }))
    : (instanceData.data?.policies ?? [])
  // Org mode: the baseline itself fills BOTH sections (applied-list view vs authoring view).
  const inherited = isOrgMode ? policies : policies.filter((p) => p.instance_key === null)
  const custom    = isOrgMode ? policies : policies.filter((p) => p.instance_key !== null)

  // Forced = every custom policy has an APPLIED override that turns it off (retained, not deleted).
  // Instance-mode concept only - org mode has no per-identity custom set to force.
  const forced = !isOrgMode && custom.length > 0 && custom.every((p) => p.override && p.override.inherit === 0 && p.override.disabled === 1)

  // Write an override, preserving the fields this control isn't changing (the "remembered" values).
  // Instance mode only - org mode uses the direct policy toggle endpoints instead.
  async function write(p: PolicyWithOverride, patch: Partial<{ inherit: boolean; disabled: boolean; enforcing: boolean }>) {
    if (instanceKey === null) return
    const cur = p.override ?? { inherit: 1, disabled: 0, enforcing: p.is_enforcing ? 1 : 0 }
    setBusy(p.id)
    try {
      await setPolicyOverride(orgId, p.id, {
        artefact_type: artefactType,
        instance_key: instanceKey,
        inherit: patch.inherit ?? cur.inherit === 1,
        disabled: patch.disabled ?? cur.disabled === 1,
        enforcing: patch.enforcing ?? cur.enforcing === 1,
      })
      await refetch()
    } catch (e) {
      showToast((e as Error).message, 'error')
    } finally {
      setBusy(null)
    }
  }

  // Custom-policy mutations (its own enabled/enforcing flags, not per-instance overrides).
  async function customAction(p: PolicyWithOverride, key: string, run: () => Promise<unknown>, done: string) {
    setBusy(p.id + key)
    try { await run(); await refetch(); showToast(done) }
    catch (e) { showToast((e as Error).message, 'error') }
    finally { setBusy(null) }
  }

  async function toggleForce(next: boolean) {
    if (instanceKey === null) return
    setBusy('__force')
    try {
      await forceIdentityCustom(orgId, { artefact_type: artefactType, instance_key: instanceKey, disabled: next })
      await refetch()
      showToast(next ? 'Custom policies disabled — only inherited policies apply.' : 'Custom policies re-enabled.')
    } catch (e) {
      showToast((e as Error).message, 'error')
    } finally {
      setBusy(null)
    }
  }

  if (loading) return <span className="spinner" />

  return (
    <div style={{ display: 'grid', gap: 14 }}>
      {(showCreate || editPolicy) && (
        <PolicyModal
          orgId={orgId}
          policy={editPolicy ?? undefined}
          artefactType={artefactType}
          // Org mode creates BASELINE policies (no instance_key) that apply to every identity.
          instanceKey={editPolicy || isOrgMode ? undefined : instanceKey ?? undefined}
          onClose={() => { setShowCreate(false); setEditPolicy(null) }}
          onSaved={() => { setShowCreate(false); setEditPolicy(null); refetch(); showToast(isOrgMode ? 'Org policy saved.' : 'Custom policy saved.') }}
        />
      )}
      {confirmDelete && (
        <ConfirmModal
          track="policy_override_delete_confirm"
          title={isOrgMode ? 'Delete org policy' : 'Delete custom policy'}
          message={isOrgMode
            ? `Delete org policy "${confirmDelete.name}"? It stops applying to every ${artefactType} in this org. This cannot be undone.`
            : `Delete custom policy "${confirmDelete.name}" for this ${artefactType}? This cannot be undone.`}
          confirmLabel="Delete"
          danger
          busy={busy === confirmDelete.id + '-del'}
          onConfirm={() => {
            const p = confirmDelete; setConfirmDelete(null)
            customAction(p, '-del', () => api.delete(`/api/orgs/${orgId}/policies/${p.id}`), 'Custom policy deleted.')
          }}
          onCancel={() => setConfirmDelete(null)}
        />
      )}

      {/* Additive-model summary (instance mode) / scope note (org mode). */}
      {isOrgMode ? (
        <div style={{ fontSize: '0.72rem', color: 'var(--text3)' }}>
          These policies apply to every {artefactType} in this org unless overridden per {artefactType}.
        </div>
      ) : (
        <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap', fontSize: '0.72rem', color: 'var(--text3)' }}>
          <span className="badge badge-neutral" style={{ fontSize: '0.62rem' }}>inherited {inherited.length}</span>
          <span>+</span>
          <span className="badge badge-neutral" style={{ fontSize: '0.62rem' }}>custom {custom.length}</span>
          {forced && <span className="badge badge-amber" style={{ fontSize: '0.62rem' }}>custom forced off</span>}
          <span>Inheritance is additive — inherited policies plus this {artefactType}'s custom policies both apply.</span>
        </div>
      )}

      {/* ── Inherited (org-baseline) — per-instance override controls, or (org mode) the
             applied baseline itself with direct Enable/Disable + Advisory/Enforcing ── */}
      {visibleSection !== 'custom' && (
      <div style={{ display: 'grid', gap: 8 }}>
        <div style={{ fontFamily: 'var(--mono)', fontSize: '0.62rem', color: 'var(--text3)', letterSpacing: '0.08em' }}>
          {isOrgMode ? '// org baseline' : '// inherited from org baseline'}
        </div>
        {inherited.length === 0 ? (
          <EmptyState
            icon="⬡"
            title="No org-level policies for this artefact type."
            subtitle={isOrgMode
              ? 'Create one on the Custom tab - it will apply to every identity of this type.'
              : 'Org-level policies you create will appear here to inherit or override.'}
          />
        ) : inherited.map((p) => {
          const o = p.override
          const inheriting = !o || o.inherit === 1
          const disabled = !!o && o.disabled === 1
          const enforcing = o ? o.enforcing === 1 : p.is_enforcing === 1
          const eff = isOrgMode
            ? { label: p.is_enabled ? (p.is_enforcing ? 'enforcing' : 'advisory') : 'off', inherited: true }
            : effective(p)
          return (
            <div key={p.id} style={{ display: 'flex', alignItems: 'center', gap: 12, background: 'var(--bg2)', border: '1px solid var(--border)', borderRadius: 6, padding: '8px 12px', flexWrap: 'wrap' }}>
              <div style={{ flex: 1, minWidth: 160 }}>
                <div style={{ fontSize: '0.82rem', color: 'var(--text)' }}>{p.name}</div>
                <div style={{ fontSize: '0.7rem', color: 'var(--text3)' }}>
                  effective: <span style={{ color: eff.label === 'off' ? 'var(--text3)' : 'var(--text2)' }}>{eff.label}</span>
                  {isOrgMode ? ' · org baseline' : eff.inherited ? ' · inherited' : ' · overridden'}
                </div>
              </div>

              {canAdmin && isOrgMode ? (
                // Org mode: direct policy controls (the same toggle endpoints the custom section uses).
                <div style={{ display: 'flex', alignItems: 'center', gap: 6, flexWrap: 'wrap' }}>
                  <button className="btn btn-ghost btn-sm" disabled={busy === p.id + '-en'} style={{ fontSize: '0.68rem' }}
                    onClick={() => customAction(p, '-en', () => api.post(`/api/orgs/${orgId}/policies/${p.id}/toggle`, {}), p.is_enabled ? 'Policy disabled.' : 'Policy enabled.')}>
                    {busy === p.id + '-en' ? <span className="spinner" /> : (p.is_enabled ? 'Disable' : 'Enable')}
                  </button>
                  {p.is_enabled === 1 && (
                    <button className="btn btn-ghost btn-sm" disabled={busy === p.id + '-enf'} style={{ fontSize: '0.68rem' }}
                      onClick={() => customAction(p, '-enf', () => api.post(`/api/orgs/${orgId}/policies/${p.id}/toggle-enforcing`, {}), p.is_enforcing ? 'Set to advisory.' : 'Set to enforcing.')}>
                      {busy === p.id + '-enf' ? <span className="spinner" /> : (p.is_enforcing ? 'Set advisory' : 'Set enforcing')}
                    </button>
                  )}
                </div>
              ) : canAdmin ? (
                <div style={{ display: 'flex', alignItems: 'center', gap: 10, flexWrap: 'wrap' }}>
                  <label style={{ display: 'flex', alignItems: 'center', gap: 6, fontSize: '0.72rem', color: 'var(--text2)', cursor: 'pointer' }}>
                    <input type="checkbox" checked={inheriting} disabled={busy === p.id} onChange={(e) => write(p, { inherit: e.target.checked })} />
                    Inherit
                  </label>
                  {!inheriting && (
                    <>
                      <label style={{ display: 'flex', alignItems: 'center', gap: 6, fontSize: '0.72rem', color: 'var(--text2)', cursor: 'pointer' }}>
                        <input type="checkbox" checked={disabled} disabled={busy === p.id} onChange={(e) => write(p, { disabled: e.target.checked })} />
                        Off
                      </label>
                      {!disabled && (
                        <div style={{ display: 'flex', gap: 4 }}>
                          <button className={`btn btn-sm ${enforcing ? 'btn-ghost' : 'btn-primary'}`} disabled={busy === p.id} onClick={() => write(p, { disabled: false, enforcing: false })} style={{ fontSize: '0.68rem' }}>Advisory</button>
                          <button className={`btn btn-sm ${enforcing ? 'btn-primary' : 'btn-ghost'}`} disabled={busy === p.id} onClick={() => write(p, { disabled: false, enforcing: true })} style={{ fontSize: '0.68rem' }}>Enforcing</button>
                        </div>
                      )}
                    </>
                  )}
                </div>
              ) : (
                <span className="badge badge-neutral" style={{ fontSize: '0.62rem' }}>{eff.label}</span>
              )}
            </div>
          )
        })}
      </div>
      )}

      {/* ── Custom (identity-authored) policies; org mode = org-baseline authoring ── */}
      {visibleSection !== 'inherited' && (
      <div style={{ display: 'grid', gap: 8 }}>
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 8, flexWrap: 'wrap' }}>
          <div style={{ fontFamily: 'var(--mono)', fontSize: '0.62rem', color: 'var(--text3)', letterSpacing: '0.08em' }}>
            {isOrgMode ? `// org policies for every ${artefactType}` : `// custom for this ${artefactType}`}
          </div>
          {canAdmin && (
            <div style={{ display: 'flex', gap: 6, alignItems: 'center' }}>
              {!isOrgMode && custom.length > 0 && (
                <button className="btn btn-ghost btn-sm" disabled={busy === '__force'} style={{ fontSize: '0.68rem' }} onClick={() => toggleForce(!forced)}>
                  {busy === '__force' ? <span className="spinner" /> : (forced ? 'Re-enable custom' : 'Force (disable custom)')}
                </button>
              )}
              <button className="btn btn-primary btn-sm" style={{ fontSize: '0.68rem' }} onClick={() => setShowCreate(true)}>
                {isOrgMode ? '+ Add org policy' : '+ Add custom policy'}
              </button>
            </div>
          )}
        </div>

        {custom.length === 0 ? (
          <div style={{ fontSize: '0.72rem', color: 'var(--text3)' }}>
            {isOrgMode
              ? <>No org policies for this artefact type yet.{canAdmin ? ' Add one - it applies to every ' + artefactType + ' in this org.' : ''}</>
              : <>No custom policies for this {artefactType}.{canAdmin ? ' Add one to layer identity-specific rules on top of the inherited baseline.' : ''}</>}
          </div>
        ) : custom.map((p) => {
          const eff = effective(p)
          const off = forced || p.is_enabled === 0
          const label = off ? 'off' : (p.is_enforcing ? 'enforcing' : 'advisory')
          return (
            <div key={p.id} style={{ display: 'flex', alignItems: 'center', gap: 12, background: 'var(--bg2)', border: '1px solid var(--border)', borderRadius: 6, padding: '8px 12px', flexWrap: 'wrap' }}>
              <div style={{ flex: 1, minWidth: 160 }}>
                <div style={{ fontSize: '0.82rem', color: 'var(--text)' }}>{p.name}</div>
                <div style={{ fontSize: '0.7rem', color: 'var(--text3)' }}>
                  effective: <span style={{ color: label === 'off' ? 'var(--text3)' : 'var(--text2)' }}>{label}</span>
                  {isOrgMode ? ' · org baseline' : forced ? ' · forced off' : ' · custom'}
                </div>
              </div>
              {canAdmin ? (
                <div style={{ display: 'flex', alignItems: 'center', gap: 6, flexWrap: 'wrap' }}>
                  <button className="btn btn-ghost btn-sm" disabled={busy === p.id + '-en'} style={{ fontSize: '0.68rem' }}
                    onClick={() => customAction(p, '-en', () => api.post(`/api/orgs/${orgId}/policies/${p.id}/toggle`, {}), p.is_enabled ? 'Policy disabled.' : 'Policy enabled.')}>
                    {busy === p.id + '-en' ? <span className="spinner" /> : (p.is_enabled ? 'Disable' : 'Enable')}
                  </button>
                  <button className="btn btn-ghost btn-sm" disabled={busy === p.id + '-enf'} style={{ fontSize: '0.68rem' }}
                    onClick={() => customAction(p, '-enf', () => api.post(`/api/orgs/${orgId}/policies/${p.id}/toggle-enforcing`, {}), p.is_enforcing ? 'Set to advisory.' : 'Set to enforcing.')}>
                    {busy === p.id + '-enf' ? <span className="spinner" /> : (p.is_enforcing ? 'Set advisory' : 'Set enforcing')}
                  </button>
                  <button className="btn btn-ghost btn-sm" style={{ fontSize: '0.68rem' }} onClick={() => setEditPolicy(p)}>Edit</button>
                  <button className="btn btn-danger btn-sm" style={{ fontSize: '0.68rem' }} onClick={() => setConfirmDelete(p)}>Delete</button>
                </div>
              ) : (
                <span className="badge badge-neutral" style={{ fontSize: '0.62rem' }}>{eff.label}</span>
              )}
            </div>
          )
        })}
      </div>
      )}
    </div>
  )
}
